import { Col, Row, Tooltip } from 'antd'
import classNames from 'classnames'
import { atom, useAtom } from 'jotai'
import { observer } from 'mobx-react'
import { useEffect, useMemo, useState } from 'react'
import { IconRobot, MagicDice } from 'wujieai-react-icon'
import { CDNImage } from '~/components/business/CDNImage'
import { AutoScroll } from '~/components/ui/AutoScroll'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyModal } from '~/components/ui/MyModal'
import {
  GET_DICE_RESULT,
  GET_DICE_THEME,
  GET_MIND_DICE_RESULT,
} from '~/graphqls/gqls'
import {
  AiCreateArtworkModel,
  Language,
  getDiceResult,
  getDiceResultVariables,
  getDiceResult_commonQuery_magicDiceSearch,
  getDiceTheme,
  getDiceTheme_commonQuery_magicDiceThemeList,
  getMindDiceResult,
  getMindDiceResultVariables,
  getMindDiceResult_commonQuery_smartMagicSearch,
} from '~/graphqls/types'
import { useDidUpdateEffect } from '~/hooks/useDidUpdateEffect'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { useStores } from '~/hooks/useStores'
import { getStorage, isNullOrUndefined, setStorage, trackEvent } from '~/utils'
import {
  modelCodeAtom,
  promptAtom,
  selectModelAtom,
  selectSizeAtom,
} from '../ai-form/atom'
import { setAgainParams } from '../utils'
import useOptions from './useOptions'
import { toLower } from 'lodash'
import { useType } from '../useType'

export const diceThemeAtom = atom(0)

export const Dice = observer(() => {
  const { store } = useStores()
  const [selectModel, setSelectModel] = useAtom(selectModelAtom)
  const [, setModelCode] = useAtom(modelCodeAtom)
  const [prompt, setPrompt] = useAtom(promptAtom)
  const [selectSize] = useAtom(selectSizeAtom)
  const { curModelThemeOptions } = useOptions()
  const { isST, isMJ } = useType()

  const [isEnglish, setIsEnglish] = useState(false)
  const [isOnlyPrompt, setIsOnlyPrompt] = useState(false)
  const delay = isOnlyPrompt ? 1500 : 2500
  // 只有空->有值，并且点击骰子才会变更一次的 prompt，用于处理下面的 keyword 传参
  const [oncePrompt, setOncePrompt] = useState<string | null>(null)
  const [keyword, setKeyword] = useState<string | null>('')
  // 已经被选中过的为new的骰子主题
  const [selectedNewDiceThemeIds, setSelectedNewDiceThemeIds] = useState<
    any | null
  >([])

  const [themeList, setThemeList] = useState<
    getDiceTheme_commonQuery_magicDiceThemeList[]
  >([])
  const [theme, setTheme] = useAtom(diceThemeAtom)
  /** 用户点击主题分类，如果分类上配置了关联的模型，自动选择对应的模型 */
  const _setTheme = (it: getDiceTheme_commonQuery_magicDiceThemeList) => {
    setTheme(it.id)
    // 如果当前是风格模型，不做任何处理
    if (selectModel === AiCreateArtworkModel.STYLE) return
    if (it.model && it.modelCode) {
      setSelectModel(it.model)
      setModelCode(it.modelCode)
    }
  }
  /** 如果魔法骰子请求到的模型与分类模型不一致，以分类模型为准 */
  const modelCodeFromTheme = useMemo(() => {
    const t = themeList.find(it => it.id === theme)
    if (t?.model && t.modelCode) {
      return t.modelCode
    }
    return undefined
  }, [theme, themeList])

  const [tempTheme, setTempTheme] = useState<number>(0)
  const [prompts, setPrompts] = useState<{
    en: string | null
    zh: string | null
  }>()

  const [loading, setLoading] = useState(false)
  const [mindLoading, setMindLoading] = useState(false)
  const [animate, setAnimate] = useState(false)
  const [mindAnimate, setMindAnimate] = useState(false)
  const [animateKey, setAnimateKey] = useState('')
  const [arriveRight, setArriveRight] = useState(false)
  const [themeModalVisible, setThemeModalVisible] = useState(false)

  const getThemeList = useImperativeQuery<getDiceTheme>(GET_DICE_THEME)
  const searchPrompt = useImperativeQuery<
    getDiceResult,
    getDiceResultVariables
  >(GET_DICE_RESULT)
  const searchMindPrompt = useImperativeQuery<
    getMindDiceResult,
    getMindDiceResultVariables
  >(GET_MIND_DICE_RESULT)

  useEffect(() => {
    try {
      getThemeList().then(res => {
        setThemeList([
          {
            name: '全部',
            id: 0,
          },
          ...(res?.data?.commonQuery?.magicDiceThemeList || []),
        ] as getDiceTheme_commonQuery_magicDiceThemeList[])
      })
    } catch (error) {}
    // 获取已经选中过的new骰子主题
    const storedSelectedNewDiceThemeIds = getStorage(
      'selectedNewDiceThemeIds'
    ) as (any | null)[]
    if (
      storedSelectedNewDiceThemeIds &&
      storedSelectedNewDiceThemeIds.length > 0
    ) {
      setSelectedNewDiceThemeIds(storedSelectedNewDiceThemeIds)
    } else {
      setSelectedNewDiceThemeIds([])
    }
  }, [])

  useEffect(() => {
    if (isEnglish) {
      setPrompt(_p => prompts?.en ?? _p)
    } else {
      setPrompt(_p => prompts?.zh ?? _p)
    }
  }, [isEnglish, prompts])

  // 清空输入，关键词也清空
  useEffect(() => {
    if (!!prompt.length) return
    setPrompts({
      en: null,
      zh: null,
    })
    setOncePrompt(null)
  }, [prompt])

  useEffect(() => {
    setKeyword(oncePrompt)
  }, [oncePrompt])

  useEffect(() => {
    setTempTheme(theme)
  }, [theme])

  //记录主题和设置
  useDidUpdateEffect(() => {
    setStorage('diceSetting', {
      theme,
      isEnglish,
      isOnlyPrompt,
    })
  }, [theme, isEnglish, isOnlyPrompt])

  //恢复设置
  useEffect(() => {
    const setting: any = getStorage('diceSetting')
    setIsEnglish(setting?.isEnglish || false)
    setTheme(setting?.theme || 0)
    setIsOnlyPrompt(setting?.isOnlyPrompt || false)
  }, [])

  const MJID = themeList.filter(it => toLower(it.name!) === toLower('MJ'))?.[0]
    ?.id
  useEffect(() => {
    if (isMJ && !!MJID) {
      setTheme(MJID)
    }
  }, [selectModel, MJID])

  const onDice = () => {
    if (loading || animate) return
    trackEvent({
      category: '魔法骰子',
      label: '点击骰子按钮',
    })
    let text = ''
    if (isNullOrUndefined(oncePrompt)) {
      setOncePrompt(prompt ?? '')
      text = prompt ?? ''
    } else {
      text = keyword ?? ''
    }
    try {
      const timestamp = new Date().getTime()
      setLoading(true)
      setAnimate(true)
      setAnimateKey(new Date().getTime().toString())
      setTimeout(() => {
        setAnimate(false)
      }, delay)
      searchPrompt({
        param: {
          keyword: text,
          language: isEnglish ? Language.ENGLISH : Language.CHINESE,
          model: selectModel,
          themeId: !!theme ? theme : undefined,
        },
        onlyPrompt: isOnlyPrompt,
      })
        .then(res => {
          const data = res?.data?.commonQuery
            ?.magicDiceSearch as getDiceResult_commonQuery_magicDiceSearch
          const timestamp2 = new Date().getTime()
          setTimeout(() => {
            setMagicResult(data)
          }, delay - (timestamp2 - timestamp))
        })
        .finally(() => {
          setLoading(false)
        })
    } catch (error) {}
  }

  const onMindDice = () => {
    if (mindLoading || mindAnimate) return
    trackEvent({
      category: '智能魔法',
      label: '点击智能魔法按钮',
    })
    let text = ''
    if (isNullOrUndefined(oncePrompt)) {
      setOncePrompt(prompt ?? '')
      text = prompt ?? ''
    } else {
      text = keyword ?? ''
    }
    try {
      const timestamp = new Date().getTime()
      setMindLoading(true)
      setMindAnimate(true)
      setAnimateKey(new Date().getTime().toString())
      setTimeout(() => {
        setMindAnimate(false)
      }, delay)
      searchMindPrompt({
        param: {
          keyword: text,
          model: selectModel,
          theme: !!theme ? theme : undefined,
        },
        onlyPrompt: isOnlyPrompt,
      })
        .then(res => {
          const data = res?.data?.commonQuery
            ?.smartMagicSearch as getMindDiceResult_commonQuery_smartMagicSearch
          const timestamp2 = new Date().getTime()
          setTimeout(() => {
            setMagicResult(data, 'mind')
          }, delay - (timestamp2 - timestamp))
        })
        .finally(() => {
          setMindLoading(false)
        })
    } catch (error) {}
  }

  const setMagicResult = (
    data:
      | getDiceResult_commonQuery_magicDiceSearch
      | getMindDiceResult_commonQuery_smartMagicSearch,
    type?: 'normal' | 'mind'
  ) => {
    setPrompts({
      zh: data?.promptChinese as string,
      en: data?.promptEnglish as string,
    })
    if (!isST && !isOnlyPrompt) {
      /** 如果魔法骰子请求到的模型与分类模型不一致，以分类模型为准 */
      const modelCode = modelCodeFromTheme ?? data?.modelCode

      const curModelThemeOption = curModelThemeOptions?.filter(
        it => it.modelCode === modelCode
      )?.[0]
      const param = {
        /** 兼容 modecode 对尺寸的影响 */
        resolutionKey: curModelThemeOptions?.filter(
          it => it.modelCode === modelCode
        )?.[0]?.resolutionKey!,
        size: selectSize,
        prompt: isEnglish
          ? (data?.promptEnglish as string)
          : (data?.promptChinese as string),

        model: selectModel,
        characterOptions:
          data?.characterOptions?.map(it => ({
            key: it?.key!,
            name: it?.name!,
          })) || [],
        modelFusionOptions:
          data?.modelFusionOptions?.map(it => ({
            key: it?.key!,
            weight: it?.weight!,
          })) || [],
        styleDecorationOptions:
          data?.styleDecorationOptions?.map(it => ({
            key: it?.key!,
            type: it?.type!,
          })) || [],
        modelCode: modelCode,
        cfg: data?.cfg ?? curModelThemeOption?.cfg ?? undefined,
        // 展示modelcode 默认的配置
        ensd: curModelThemeOption?.ensd ?? undefined,
        clipSkip: curModelThemeOption?.clipSkip ?? undefined,
        samplerModelInfo: !isNullOrUndefined(curModelThemeOption?.samplerIndex)
          ? {
              samplerIndex: curModelThemeOption?.samplerIndex,
            }
          : undefined,
        trackEventLabel: JSON.stringify({
          category: type === 'mind' ? '智能魔法' : '魔法骰子',
          label: 'AI创作-立即生成',
        }),
      }
      setAgainParams(param as any)
      store.ai?.setIsAgain(true)
    }
  }

  const activeIndex = useMemo(() => {
    return themeList.findIndex(it => it.id === theme)
  }, [themeList, theme])

  if (!MJID && isMJ) {
    return <></>
  }

  return (
    <>
      <div className="mt-2 flex justify-between items-center h-[28px]">
        <div className="w-[448px] relative">
          {!arriveRight && (
            <div
              className="w-[88px] h-5 absolute right-0 z-10 pointer-events-none"
              style={{
                background:
                  'linear-gradient(270deg, #141416 0%, rgba(20, 20, 22, 0.00) 100%)',
              }}
            ></div>
          )}
          <AutoScroll
            activeIndex={activeIndex}
            setArriveRight={setArriveRight}
            gutter={8}
            className="max-w-[448px]"
          >
            <div className="flex">
              {themeList.map(it => (
                <div
                  key={it.id}
                  className={classNames(
                    'relative text-xs text-neutral-400 leading-none px-2 py-1 rounded-full  bg-neutral-200 mr-2 last:mr-0 cursor-pointer whitespace-nowrap',
                    {
                      '!bg-primary !text-neutral-900': it.id === theme,
                      'cursor-not-allowed': isMJ && it.id !== theme,
                    }
                  )}
                  onClick={() => {
                    if (isMJ && it.id !== theme) return
                    _setTheme(it)
                    setKeyword('')
                    setSelectedNewDiceThemeIds(
                      (prevSelected: (any | null)[]) => {
                        if (it.isNew && !prevSelected.includes(it.id)) {
                          const newSelectedIds = [...prevSelected, it.id]
                          setStorage('selectedNewDiceThemeIds', newSelectedIds)
                          return newSelectedIds
                        }
                        return prevSelected
                      }
                    )
                  }}
                >
                  {it.isNew && !selectedNewDiceThemeIds.includes(it.id) && (
                    <span className="absolute h-[5px] w-[5px] top-0 right-0 rounded-full bg-[#ef466f]"></span>
                  )}
                  {it.name}
                </div>
              ))}
            </div>
          </AutoScroll>
        </div>
        <div
          className={classNames(
            'py-[2px] px-[4px] rounded bg-neutral-200 flex items-center text-xs leading-4 text-center cursor-pointer',
            {
              'cursor-not-allowed': isMJ,
            }
          )}
          onClick={() => {
            if (isMJ) return
            setThemeModalVisible(true)
          }}
        >
          <CDNImage
            src="/images/ai/more_grid.svg"
            className="w-[12px] h-[12px]"
          />
          <div className="text-neutral-400 ml-[4px]">更多</div>
        </div>

        <div className="p-[2px] rounded bg-neutral-200 flex items-center text-xs leading-4 text-center cursor-pointer">
          <div
            className={classNames(
              'w-[28px] h-4 rounded-sm text-xs leading-4 font-medium',
              {
                'bg-primary text-neutral-800': !isOnlyPrompt,
              }
            )}
            onClick={() => setIsOnlyPrompt(false)}
          >
            全参
          </div>
          <div
            className={classNames(
              'w-[28px] h-4 rounded-sm mr-1 text-xs leading-4 font-medium',
              {
                'bg-primary text-neutral-800': isOnlyPrompt,
              }
            )}
            onClick={() => setIsOnlyPrompt(true)}
          >
            纯咒
          </div>
        </div>
        <div className="p-[2px] rounded bg-neutral-200 flex items-center text-xs leading-4 text-center cursor-pointer">
          <div
            className={classNames(
              'w-4 h-4 rounded-sm mr-1 text-xs leading-4 font-medium',
              {
                'bg-primary text-neutral-800': !isEnglish,
              }
            )}
            onClick={() => setIsEnglish(false)}
          >
            中
          </div>
          <div
            className={classNames(
              'w-4 h-4 rounded-sm text-xs leading-4 font-medium',
              {
                'bg-primary text-neutral-800': isEnglish,
              }
            )}
            onClick={() => setIsEnglish(true)}
          >
            英
          </div>
        </div>
        <div className="bg-[#353945] w-[1px] h-4"></div>
        <div className="flex">
          <div
            className="text-neutral-800 text-xs font-normal flex items-center cursor-pointer"
            onClick={onDice}
          >
            {animate ? (
              <CDNImage
                src={`/images/ai/magicdice.png?ts=${animateKey}`}
                className="w-6 h-6"
                alt="dice"
              />
            ) : (
              <Tooltip
                placement="bottom"
                title="魔法骰子：按主题随机生成描述词与模型参数"
                zIndex={998}
                mouseLeaveDelay={0}
              >
                <MagicDice size={24} />
              </Tooltip>
            )}
          </div>
          <div
            className="text-neutral-800 text-xs font-normal flex items-center cursor-pointer ml-2"
            onClick={onMindDice}
          >
            {mindAnimate ? (
              <CDNImage
                src={`/images/ai/magicdice2.png?ts=${animateKey}`}
                className="w-6 h-6"
                alt="dice"
              />
            ) : (
              <Tooltip
                placement="bottom"
                title="智能魔法：根据输入内容，按主题智能输出拓展描述词与模型参数"
                zIndex={998}
                mouseLeaveDelay={0}
              >
                <IconRobot size={24} />
              </Tooltip>
            )}
          </div>
        </div>
      </div>
      {animate && (
        <div className="absolute z-20 bg-neutral-100 top-0.5 left-0.5 right-0.5 bottom-[40px] rounded-xl overflow-hidden">
          <div
            className="w-full h-full flex justify-center items-center"
            style={{
              background:
                'linear-gradient(180deg, rgba(120, 81, 236, 0.38) 0%, rgba(120, 81, 236, 0.00) 100%)',
            }}
          >
            {isOnlyPrompt ? (
              <CDNImage
                src={`/images/ai/magicdice_input_mormal.png?ts=${animateKey}`}
                className="block h-[100px] mx-auto"
                alt="dice"
              />
            ) : (
              <CDNImage
                src={`/images/ai/magicdice_input_mormal_2.png?ts=${animateKey}`}
                className="block w-[740px] mx-auto"
                alt="dice"
              />
            )}
          </div>
        </div>
      )}
      {mindAnimate && (
        <div className="absolute z-20 bg-neutral-100 top-0.5 left-0.5 right-0.5 bottom-[40px] rounded-xl overflow-hidden">
          <div
            className="w-full h-full flex justify-center items-center"
            style={{
              background:
                'linear-gradient(180deg, rgba(120, 81, 236, 0.38) 0%, rgba(120, 81, 236, 0.00) 100%)',
            }}
          >
            {isOnlyPrompt ? (
              <CDNImage
                src={`/images/ai/magicdice_input_mind.png?ts=${animateKey}`}
                className="block h-[100px] mx-auto"
                alt="dice"
              />
            ) : (
              <CDNImage
                src={`/images/ai/magicdice_input_mind_2.png?ts=${animateKey}`}
                className="block w-[740px] mx-auto"
                alt="dice"
              />
            )}
          </div>
        </div>
      )}
      <MyModal
        dark
        width={720}
        title={<>主题</>}
        footer={null}
        open={themeModalVisible}
        onCancel={() => {
          setTempTheme(theme)
          setThemeModalVisible(false)
        }}
        maskStyle={{
          backgroundColor: 'rgba(0,0,0,0.8)',
        }}
        centered
      >
        <div
          className="flex flex-wrap content-start mb-3 min-h-[100px] scrollbar overflow-y-auto gap-3"
          style={{ height: `calc(100vh - 216px)` }}
        >
          {themeList.map(item => {
            const isActive = tempTheme === item.id
            return (
              <div
                key={item.id}
                style={{
                  width: 'calc((100% - 40px) / 4)',
                }}
              >
                <div
                  className={classNames(
                    'relative rounded-full p-1 text-neutral-800 font-semibold text-sm border-2 text-center cursor-pointer',
                    [[isActive ? 'border-neutral-500' : 'border-transparent']]
                  )}
                  style={{
                    background: isActive
                      ? 'radial-gradient(74.76% 334.54% at 49.84% 50%, #374176 0%, rgba(55, 65, 118, 0) 100%)'
                      : '#23262F',
                  }}
                  onClick={() => {
                    setTempTheme(item.id)
                  }}
                >
                  {item.isNew && (
                    <img
                      className="h-[14px] absolute -top-1 right-0"
                      src="/images/ai/new.png"
                      alt=""
                    />
                  )}
                  <div className="overflow-y-auto">{item.name}</div>
                </div>
              </div>
            )
          })}
        </div>
        <div className="flex justify-between items-center gap-3">
          <MyBtn
            block
            type="default"
            className="!bg-neutral-200 !border-neutral-200 !text-neutral-800"
            onClick={() => {
              setTheme(0)
              setThemeModalVisible(false)
            }}
          >
            重置
          </MyBtn>
          <MyBtn
            block
            type="primary"
            onClick={() => {
              _setTheme(themeList.find(it => it.id === tempTheme)!)
              setThemeModalVisible(false)
            }}
          >
            确定
          </MyBtn>
        </div>
      </MyModal>
    </>
  )
})
